import { ChangelogArticle, ChangelogVersion, FormattedChangelog, FormattedChangelogVersion } from '@lfp-digital/back-types-mpg';
import {map, orderBy} from 'lodash';
import React, {
  CSSProperties,
  useEffect,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import {getEndpoint, getHeaders, getMPPEndpoint} from '../../../helpers/endpoint';
import { InputLabelEditor } from '../../../components/InputLabelEditor';
import { Separator } from '../Editor/Separator';

const Styles: Record<string, CSSProperties> = {
  container: {
    margin: 16,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 16,
    paddingBottom: 16,
    width: '100%',
    backgroundColor: 'white',
  },
  item: {
    verticalAlign: 'middle',
  }
};

export const getChangelog = (
  isMPP: boolean,
  onSuccess: (response: FormattedChangelog) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('getChangelog'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const deleteArticle = (
  isMPP: boolean,
  version: string | undefined,
  payload: any,
  onSuccess: (response: any) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('article', {version, uuid: payload.uuid}), {
      method: 'DELETE',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

export const getVersion = (
  isMPP: boolean,
  version: string,
  onSuccess: (response: ChangelogVersion) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('version', version), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

type OrderType = {type: string; sort: 'asc' | 'desc'};

const orderList = (
  data: ChangelogArticle[],
  order: OrderType,
) => orderBy(data, [order.type], [order.sort]);

export const ArticlesList = ({ history, match }) => {
  const dispatch = useDispatch();

  const isMPP = match?.path?.includes('mpp');

  const [order, setOrder] = useState<OrderType>({
    type: 'name',
    sort: 'desc',
  });

  const [articles, setArticles] = useState<ChangelogArticle[]>();
  const [versions, setVersions] = useState<string[]>();
  const [version, setVersion] = useState<string>();

  const onSuccess = (response: ChangelogVersion) => {
    setArticles(response?.articles);
  };

  useEffect(() => {
    if (version) {
      dispatch(getVersion(isMPP, version, onSuccess));
    }
  }, [version]);

  useEffect(() => {
    if (versions?.length) {
      setVersion(versions[0]);
    }
  }, [versions]);

  useEffect(() => {
    dispatch(
      getChangelog(isMPP, (response) =>
        setVersions(map(response.versions, 'version')),
      ),
    );
  }, []);

  useEffect(() => {
    if (articles) {
      setArticles(
        orderList(articles, order),
      );
    }
  }, [order]);

  const setOrderForNotif = (type: string) => {
    setOrder(state => {
      return {
        type,
        sort:
          state.type === type
            ? state.sort === 'asc'
              ? 'desc'
              : 'asc'
            : state.sort,
      };
    });
  };

  return (
    <div style={Styles.container}>
      {versions?.length ? (
        <>
          <InputLabelEditor title="VERSIONS LIST" />
          <div style={{flex: 1, marginRight: 8, marginBottom: 16}}>
            <select
              value={version || ''}
              className="form-control"
              onChange={e => {
                setVersion(e.target.value);
              }}>
              {versions?.map(version => (
                <option key={version} value={version}>
                  {version}
                </option>
              ))}
            </select>
          </div>
          <Separator />
          <InputLabelEditor title="ARTICLES LIST" />
          {articles?.length ? (
            <>
              <table className="table table-responsive table-striped text-center align-middle">
                <thead>
                  <tr>
                    <th
                      onClick={() => setOrderForNotif('name')}
                      style={{cursor: 'pointer'}}>
                      Name
                    </th>
                    <th
                      onClick={() => setOrderForNotif('version')}
                      style={{cursor: 'pointer'}}>
                      Tags
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {articles?.map(item => {
                    return (
                      <tr key={item.uuid}>
                        <th style={Styles.item}>{item?.name}</th>
                        <th style={Styles.item}>{item?.tags?.length ? item?.tags?.join(', ') : '-'}</th>
                        <th style={Styles.item}>
                          <button
                            style={{marginRight: 8}}
                            className="btn"
                            onClick={() =>
                              history.push(`${isMPP ? '/mpp' : ''}/article/update/version/${version}/article/${item.name}`)
                            }>
                            Modifier
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              swal({
                                title: 'Are you sure ?',
                                type: 'info',
                                showCloseButton: true,
                                showCancelButton: true,
                                focusConfirm: false,
                                confirmButtonText: 'Yes',
                                cancelButtonText: 'No',
                              }).then(() => {
                                if (version) {
                                  dispatch(
                                    deleteArticle(isMPP, version, item, (response) =>
                                      setArticles(response?.articles),
                                    ),
                                  );
                                }
                              });
                            }}>
                            <i style={{color: 'white'}} className="fa fa-trash" />
                          </button>
                        </th>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', marginTop: 16}}>
                <button className="btn btn-primary btn-flat m-4" onClick={() => history.push(`${isMPP ? '/mpp' : ''}/article/create/${version}`)}>
                  Add article
                </button>
              </div>
            </>
          ) : (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', marginTop: 16}}>
              <div style={{textAlign: 'center', fontSize: 20}}>No articles found!</div>
              <button className="btn btn-primary btn-flat m-4" onClick={() => history.push(`${isMPP ? '/mpp' : ''}/article/create/${version}`)}>
                Create article
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', marginTop: 16}}>
          <div style={{textAlign: 'center', fontSize: 20}}>No versions found!</div>
          <button className="btn btn-primary btn-flat m-4" onClick={() => history.push(`${isMPP ? '/mpp' : ''}/version/create`)}>
            Create version
          </button>
        </div>
      )}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/Articles/List/index.tsx